import { useEffect, useMemo, useRef, useState } from 'react';
import type { StreamEvent } from '@hypevault/shared-types';
import { Watermark } from './Watermark';

/**
 * Recent Activity feed — v2 port of v1's activity-ticker.ejs.
 * Vertical card with badge-icon rows, platform tag, relative timestamps.
 *
 * Config (all optional):
 *   maxEvents: number         (default 10)
 *   accentColor: string       (default '#a78bfa')
 *   showPlatform: boolean     (default true)
 *   showTimestamps: boolean   (default true)
 *   showHeader: boolean       (default true)
 *   title: string             (default 'Recent Activity')
 *   width: number (px)        (default 350)
 *   enabledEvents: string[]   subset of token names ('tip','follow','sub','sub.gift','cheer','raid','superChat','superSticker','alertPurchase','cardRedemption')
 */

type Token =
  | 'tip'
  | 'follow'
  | 'sub'
  | 'sub.gift'
  | 'cheer'
  | 'raid'
  | 'superChat'
  | 'superSticker'
  | 'alertPurchase'
  | 'cardRedemption'
  | 'channelPointRedemption';

interface Row {
  id: string;
  token: Token;
  descriptionHtml: string;
  platform: string;
  ts: number;
}

const BADGE_COLORS: Record<Token, { bg: string; fg: string }> = {
  tip: { bg: 'rgba(139, 92, 246, 0.18)', fg: '#a78bfa' },
  follow: { bg: 'rgba(16, 185, 129, 0.15)', fg: '#34d399' },
  sub: { bg: 'rgba(59, 130, 246, 0.18)', fg: '#60a5fa' },
  'sub.gift': { bg: 'rgba(236, 72, 153, 0.15)', fg: '#f472b6' },
  cheer: { bg: 'rgba(251, 146, 60, 0.15)', fg: '#fb923c' },
  raid: { bg: 'rgba(239, 68, 68, 0.15)', fg: '#f87171' },
  superChat: { bg: 'rgba(251, 191, 36, 0.15)', fg: '#fbbf24' },
  superSticker: { bg: 'rgba(251, 191, 36, 0.15)', fg: '#fbbf24' },
  alertPurchase: { bg: 'rgba(245, 158, 11, 0.15)', fg: '#fbbf24' },
  cardRedemption: { bg: 'rgba(139, 92, 246, 0.18)', fg: '#a78bfa' },
  channelPointRedemption: { bg: 'rgba(192, 132, 252, 0.18)', fg: '#c084fc' },
};

const PLATFORM_LABEL: Record<string, string> = {
  hypevault: 'HypeVault',
  twitch: 'Twitch',
  youtube: 'YouTube',
  kick: 'Kick',
};

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function relativeTime(ts: number, now: number): string {
  const diff = Math.floor((now - ts) / 1000);
  if (diff < 10) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  const m = Math.floor(diff / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function eventToRow(e: StreamEvent): Row | null {
  const ts = e.timestamp;
  const id = e.id;
  switch (e.type) {
    case 'tip':
      return {
        id,
        token: 'tip',
        ts,
        platform: PLATFORM_LABEL[e.platform] ?? e.platform,
        descriptionHtml: `<strong>${escapeHtml(e.viewerName)}</strong> tipped ${e.coins.toLocaleString()} coins`,
      };
    case 'tip_received':
      // Reuse the legacy 'tip' token so badge colors + icon stay consistent.
      return {
        id,
        token: 'tip',
        ts,
        platform: PLATFORM_LABEL[e.platform] ?? e.platform,
        descriptionHtml: `<strong>${escapeHtml(e.viewerName)}</strong> tipped $${(e.amountCents / 100).toFixed(2)}`,
      };
    case 'follow':
      return {
        id,
        token: 'follow',
        ts,
        platform: PLATFORM_LABEL[e.platform] ?? e.platform,
        descriptionHtml: `<strong>${escapeHtml(e.viewerName)}</strong> followed`,
      };
    case 'sub':
      if (e.isGift) {
        return {
          id,
          token: 'sub.gift',
          ts,
          platform: PLATFORM_LABEL[e.platform] ?? e.platform,
          descriptionHtml: `<strong>${escapeHtml(e.gifterName ?? 'Anonymous')}</strong> gifted a sub to ${escapeHtml(e.viewerName)}`,
        };
      }
      return {
        id,
        token: 'sub',
        ts,
        platform: PLATFORM_LABEL[e.platform] ?? e.platform,
        descriptionHtml: `<strong>${escapeHtml(e.viewerName)}</strong> subscribed${
          e.tier === 't2' ? ' (Tier 2)' : e.tier === 't3' ? ' (Tier 3)' : ''
        }`,
      };
    case 'cheer':
      return {
        id,
        token: 'cheer',
        ts,
        platform: PLATFORM_LABEL[e.platform] ?? e.platform,
        descriptionHtml: `<strong>${escapeHtml(e.viewerName)}</strong> cheered ${e.bits.toLocaleString()} bits`,
      };
    case 'raid':
      return {
        id,
        token: 'raid',
        ts,
        platform: PLATFORM_LABEL[e.platform] ?? e.platform,
        descriptionHtml: `<strong>${escapeHtml(e.raiderName)}</strong> raided with ${e.viewers.toLocaleString()} viewers`,
      };
    case 'superChat':
      return {
        id,
        token: 'superChat',
        ts,
        platform: PLATFORM_LABEL[e.platform] ?? e.platform,
        descriptionHtml: `<strong>${escapeHtml(e.viewerName)}</strong> Super Chat $${(e.amountCents / 100).toFixed(2)}`,
      };
    case 'superSticker':
      return {
        id,
        token: 'superSticker',
        ts,
        platform: PLATFORM_LABEL[e.platform] ?? e.platform,
        descriptionHtml: `<strong>${escapeHtml(e.viewerName)}</strong> sent a Super Sticker $${(e.amountCents / 100).toFixed(2)}`,
      };
    case 'alertPurchase':
      return {
        id,
        token: 'alertPurchase',
        ts,
        platform: PLATFORM_LABEL[e.platform] ?? e.platform,
        descriptionHtml: `<strong>${escapeHtml(e.viewerName)}</strong> bought ${escapeHtml(e.productName)}`,
      };
    case 'cardRedemption':
      return {
        id,
        token: 'cardRedemption',
        ts,
        platform: PLATFORM_LABEL[e.platform] ?? e.platform,
        descriptionHtml: `<strong>${escapeHtml(e.viewerName)}</strong> redeemed ${escapeHtml(e.cardName)}`,
      };
    case 'channelPointRedemption':
      return {
        id,
        token: 'channelPointRedemption',
        ts,
        platform: PLATFORM_LABEL[e.platform] ?? e.platform,
        descriptionHtml: `<strong>${escapeHtml(e.viewerName)}</strong> redeemed ${escapeHtml(
          e.rewardTitle,
        )} <span style="opacity:0.6">· ${e.rewardCost.toLocaleString()} pts</span>`,
      };
  }
}

function BadgeIcon({ token }: { token: Token }) {
  const common = {
    width: 14,
    height: 14,
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
  } as const;
  switch (token) {
    case 'follow':
      return (
        <svg {...common} viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
          />
        </svg>
      );
    case 'sub':
    case 'superChat':
      return (
        <svg {...common} viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
          />
        </svg>
      );
    case 'sub.gift':
    case 'alertPurchase':
      return (
        <svg {...common} viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
          />
        </svg>
      );
    case 'cheer':
      return (
        <svg {...common} viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
          />
        </svg>
      );
    case 'raid':
      return (
        <svg {...common} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
    case 'superSticker':
      return (
        <svg {...common} viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12h6M12 9v6M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      );
    case 'tip':
      return (
        <svg {...common} viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      );
    case 'cardRedemption':
    default:
      return (
        <svg {...common} viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
      );
  }
}

export function ActivityTickerWidget({
  fields,
  latestEvent,
  streamerTier = 'free',
}: {
  fields: Record<string, unknown>;
  latestEvent: StreamEvent | null;
  streamerTier?: 'free' | 'paid';
}) {
  const maxEvents = Number(fields.maxEvents ?? 10);
  const accentColor = String(fields.accentColor ?? '#a78bfa');
  const showPlatform = (fields.showPlatform as boolean | undefined) ?? true;
  const showTimestamps = (fields.showTimestamps as boolean | undefined) ?? true;
  const showHeader = (fields.showHeader as boolean | undefined) ?? true;
  const title = String(fields.title ?? 'Recent Activity');
  const width = Number(fields.width ?? 350);
  const enabledEvents = useMemo(
    () => new Set((fields.enabledEvents as string[]) ?? null),
    [fields.enabledEvents],
  );
  const hasFilter = (fields.enabledEvents as string[] | undefined)?.length;

  const [rows, setRows] = useState<Row[]>([]);
  const [now, setNow] = useState(Date.now());
  const lastId = useRef<string | null>(null);

  useEffect(() => {
    if (!latestEvent || latestEvent.id === lastId.current) return;
    lastId.current = latestEvent.id;
    const row = eventToRow(latestEvent);
    if (!row) return;
    if (hasFilter && !enabledEvents.has(row.token)) return;
    setRows((prev) => [row, ...prev].slice(0, maxEvents));
  }, [latestEvent, hasFilter, enabledEvents, maxEvents]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      style={{
        position: 'relative',
        width,
        fontFamily: "'Poppins', system-ui, sans-serif",
        color: 'white',
        borderRadius: 16,
        background: 'linear-gradient(135deg, rgba(15, 12, 30, 0.93), rgba(22, 16, 46, 0.93))',
        border: '1px solid rgba(139, 92, 246, 0.22)',
        boxShadow:
          '0 8px 32px rgba(0,0,0,0.55), 0 0 18px rgba(139,92,246,0.08), inset 0 1px 0 rgba(255,255,255,0.04)',
        overflow: 'hidden',
      }}
    >
      {showHeader && (
        <div
          style={{
            padding: '12px 16px 11px',
            background: 'linear-gradient(135deg, rgba(139,92,246,0.18), rgba(88,28,235,0.12))',
            borderBottom: '1px solid rgba(139,92,246,0.18)',
            display: 'flex',
            alignItems: 'center',
            gap: 9,
          }}
        >
          <svg
            width={20}
            height={20}
            viewBox="0 0 24 24"
            fill="none"
            stroke={accentColor}
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: 'uppercase',
              color: accentColor,
            }}
          >
            {title}
          </div>
          <div
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: accentColor,
              marginLeft: 'auto',
              animation: 'at-pulse 2.4s ease-in-out infinite',
            }}
          />
        </div>
      )}
      <div style={{ padding: '6px 0 4px', minHeight: 48 }}>
        {rows.length === 0 && (
          <div
            style={{
              padding: '20px 16px',
              textAlign: 'center',
              color: 'rgba(255,255,255,0.28)',
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            Waiting for activity…
          </div>
        )}
        {rows.map((r, i) => {
          const colors = BADGE_COLORS[r.token];
          return (
            <div
              key={r.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '7px 14px',
                animation: 'at-row-in 0.38s cubic-bezier(0.22,1,0.36,1) both',
                borderTop: i === 0 ? 'none' : '1px solid rgba(139,92,246,0.08)',
              }}
            >
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  background: colors.bg,
                  color: colors.fg,
                }}
              >
                <BadgeIcon token={r.token} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: 1.35,
                    color: 'rgba(255,255,255,0.92)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  dangerouslySetInnerHTML={{ __html: r.descriptionHtml }}
                />
                {showPlatform && (
                  <div
                    style={{
                      fontSize: 9,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: 0.6,
                      color: 'rgba(255,255,255,0.28)',
                      marginTop: 1,
                    }}
                  >
                    {r.platform}
                  </div>
                )}
              </div>
              {showTimestamps && (
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 500,
                    color: 'rgba(255,255,255,0.25)',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {relativeTime(r.ts, now)}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {streamerTier === 'free' && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '4px 12px 8px',
            borderTop: '1px solid rgba(139,92,246,0.08)',
          }}
        >
          <Watermark variant="inline" />
        </div>
      )}
      <style>{`
        @keyframes at-pulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 0 0 ${accentColor}80; }
          50% { opacity: 0.6; box-shadow: 0 0 0 4px ${accentColor}00; }
        }
        @keyframes at-row-in {
          from { opacity: 0; transform: translateY(-10px); max-height: 0; padding-top: 0; padding-bottom: 0; }
          to { opacity: 1; transform: translateY(0); max-height: 60px; padding-top: 7px; padding-bottom: 7px; }
        }
      `}</style>
    </div>
  );
}
